import type { KeywordDomain, KeywordDomains, KeywordGroup } from '@brand24-original/llm-types';

/** Domena z nazwą marki, do której należy — przy połączonej marce to może być jej wariant. */
export type OwnedKeywordDomain = KeywordDomain & { owner: string };

/**
 * Co pokazać z listy: domeny potwierdzone w TYM projekcie (dowody z odpowiedzi albo ręczne dopisanie).
 * Słownik jest kluczowany nazwą, więc reszta to zwykle inne firmy o tej nazwie. Gdy potwierdzonej nie
 * ma żadnej (stare odpowiedzi nie niosą domeny), zostaje cała lista — oznaczona, nie schowana: brak
 * dowodu to nie zaprzeczenie.
 */
export const visibleDomains = <T extends KeywordDomain>(domains: T[]): { shown: T[]; confirmed: boolean } => {
  const active = domains.filter((domain) => !domain.isExcluded);
  const confirmed = active.filter((domain) => domain.isConfirmed);
  return confirmed.length > 0 ? { shown: confirmed, confirmed: true } : { shown: active, confirmed: false };
};

/** Domeny projektu z `GET /monitor/keywords/domains`, po nazwie marki. */
export const domainsByKeywordOf = (keywordDomains: KeywordDomains[]): Map<string, KeywordDomain[]> =>
  new Map(keywordDomains.map((entry) => [entry.normalizedKeyword, entry.domains]));

/**
 * Domeny marki to suma domen WSZYSTKICH jej nazw — połączone warianty wnoszą swoje adresy.
 * `owner` to nazwa, do której domena należy: wykluczenie zapisuje się przy niej, nie przy grupie.
 */
export const groupDomainsOf = (
  group: KeywordGroup,
  domainsByKeyword: Map<string, KeywordDomain[]>
): OwnedKeywordDomain[] => {
  const seen = new Set<string>();
  return [group, ...group.aliases].flatMap((member) =>
    (domainsByKeyword.get(member.normalizedKeyword) ?? [])
      .filter((entry) => !seen.has(entry.domain) && seen.add(entry.domain))
      .map((entry) => ({ ...entry, owner: member.normalizedKeyword }))
  );
};

/**
 * Domena do ikony marki: pierwsza pokazywana (ta sama reguła co chipy — potwierdzone w projekcie przed
 * resztą słownika); bez domeny `undefined` i `BrandIcon` rysuje kolorową literkę.
 */
export const primaryDomainOf = (
  group: KeywordGroup,
  domainsByKeyword: Map<string, KeywordDomain[]>
): string | undefined => visibleDomains(groupDomainsOf(group, domainsByKeyword)).shown[0]?.domain;
